import Tithe from "#/components/exam-results/Tithe";
import { type MultiLessonFlags } from "#/components/exam-results/types";
import { type Topic } from "#/components/exam-results/views/type-2/overall";
import { type MainResultSet } from "#/services/rest/gwt/exam-results/views/type-1/multi-user.types";
import clsx from "clsx";
import type React from "react";
import { memo, useMemo } from "react";
import styles from "./styles.module.scss";

type MultiLessonTableProps = {
  activeCategory: Topic;
  participantNo: number;
  data: MainResultSet[];
};

const COLUMN_DEFS = [
  {
    key: "name",
    label: (cat: Topic) => `نام ${cat === "book" ? "کتاب" : "گروه درسی"}`
  },
  { key: "percent", label: "درصد" },
  {
    key: "tithe",
    label: "دهک",
    conditional: ({ hasTithe }: MultiLessonFlags) => hasTithe
  },
  { key: "correct", label: "درست" },
  { key: "incorrect", label: "غلط" },
  { key: "notAnswered", label: "نزده" },
  {
    key: "rank",
    label: "رتبه",
    conditional: ({ isMulti }: MultiLessonFlags) => isMulti
  },
  {
    key: "score",
    label: "تراز",
    conditional: ({ isMulti }: MultiLessonFlags) => isMulti
  }
];

const TableRow = memo(
  ({ flags, data }: { flags: MultiLessonFlags; data: MainResultSet }) => {
    return (
      <div
        className={clsx(
          styles["multiLessonTable__content-row"],
          flags.hasTithe
            ? styles["multiLessonTable__content-row--withTithe"]
            : flags.isMulti
              ? styles["multiLessonTable__content-row--noTithe"]
              : styles["multiLessonTable__content-row--default"]
        )}
      >
        <div>{data.name}</div>
        <div style={{ direction: "ltr" }}>{data.mark} %</div>
        {flags.hasTithe && (
          <div>
            <Tithe tithe={data.tierNO} />
          </div>
        )}
        <div>{data.correctAnswers}</div>
        <div>{data.incorrectAnswers}</div>
        <div>{data.noAnswers}</div>
        {flags.isMulti ? (
          <>
            <div>{data.rank}</div>
            <div>{data.level}</div>
          </>
        ) : (
          <></>
        )}
      </div>
    );
  }
);

const MultiLessonTable: React.FC<MultiLessonTableProps> = ({
  activeCategory,
  participantNo,
  data
}) => {
  const flags = useMemo<MultiLessonFlags>(
    () => ({
      isMulti: participantNo > 1,
      hasTithe: participantNo >= 10
    }),
    [participantNo]
  );

  const headers = useMemo(
    () =>
      COLUMN_DEFS.filter((col) =>
        col.conditional ? col.conditional(flags) : true
      ),
    [flags]
  );

  return (
    <div className={styles.multiLessonTable}>
      <div
        className={clsx(
          styles["multiLessonTable__header"],
          flags.hasTithe
            ? styles["multiLessonTable__content-row--withTithe"]
            : flags.isMulti
              ? styles["multiLessonTable__content-row--noTithe"]
              : styles["multiLessonTable__content-row--default"]
        )}
      >
        {headers.map((col) => (
          <span key={col.key}>
            {typeof col.label === "function"
              ? col.label(activeCategory)
              : col.label}
          </span>
        ))}
      </div>

      <div className={styles.multiLessonTable__content}>
        {data.map((row) => (
          <TableRow data={row} key={row.id} flags={flags} />
        ))}
      </div>
    </div>
  );
};

TableRow.displayName = "MultiLessonTableRow";

export default memo(MultiLessonTable);
